<template>

  <wgu-module-card v-bind="$attrs" :moduleName="moduleName" class="wgu-insurewin" :icon="icon" :minWidth="'450px'"
    v-on:visibility-change="show">
    <template v-slot:wgu-win-toolbar>
    </template>

    <v-card-actions>
      <ks-insure-result :insureType="insureType" :trusteeLands="trusteeLands" @submit="trusteeSubmit"
        @refreshTrusteeLands="handleRefreshTrusteeLands" />
    </v-card-actions>
  </wgu-module-card>
</template>

<script>
import ModuleCard from './../modulecore/ModuleCard';
import { Mapable } from '../../mixins/Mapable';
import { WguEventBus } from '../../WguEventBus';
import PolicyResult from './PolicyResult';
// Removed drawing related controller & OL style imports for query-only mode
import Option from '../../const/Option.js';
import { mapState } from 'vuex';
import axios from '@/axios';

export default {
  name: 'ks-policytool-win',
  inheritAttrs: false,
  components: {
    'wgu-module-card': ModuleCard,
    'ks-insure-result': PolicyResult
  },
  mixins: [Mapable],
  props: {
    icon: { type: String, required: false, default: 'photo_size_select_small' },
    showAngleTool: { type: Boolean, required: false, default: false },
    iconsOnly: { type: Boolean, required: false, default: false }
  },
  data () {
    return {
      date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
      moduleName: 'ks-policytool',
      // Removed measureGeom & map drawing state (query-only)
      insureType: 'searchPlot',
      singleClickKey: null,
      insureLayer: null,
      selectedItem: {
        id: '',
        land_name: '',
        owner_name: '',
        address: '',
        area: '',
        crop_id: '',
        irrigation: '',
        seed_id: '',
        mature_days: '',
        fertilizer_id: '',
        pesticide_id: '',
        insured_id: '',
        insured_name: ''
      },
      trusteeLands: [],
      option: Option,
      policyInfo: null
    }
  },
  beforeDestroy () {
    this.clearAllMapListeners();
  },
  // 移除非标准生命周期方法
  destroyed () {
    // 使用标准的destroyed替代非标准的destroy方法
    console.log('PolicyWin destroyed');
  },
  watch: {
    insureType () {
      // In query-only mode we don't support drawing, just ensure click handler for searching remains
      if (this.insureType === 'searchPlot') {
        this.onMapClickBound();
      } else {
        this.clearSingleClickListener();
      }
    }
  },
  computed: {
    ...mapState(['selectedArea', 'selectedYear'])
  },
  methods: {
    /**
     * Clears the single click listener
     */
    clearSingleClickListener () {
      if (this.singleClickKey) {
        this.map.un(this.singleClickKey.type, this.singleClickKey.listener);
        this.singleClickKey = null;
      }
    },
    /**
     * Clears all map listeners related to this component
     */
    clearAllMapListeners () {
      this.clearSingleClickListener();
    },
    /**
       * (Un-)Register map interactions when the visibility of the module changes.
       *
       * @param  {boolean} visible New visibility state
       */
    show (visible) {
      if (visible && this.insureType === 'searchPlot') {
        this.onMapClickBound();
      } else if (!visible) {
        this.clearAllMapListeners();
      }
      // Notify map about policy tool visibility so it can control plot/underwritten layers
      WguEventBus.$emit('policytool:visibility', visible);
    },
    /**
       * This function is executed, after the map is bound (see mixins/Mapable)
       */
    onMapBound () {
      if (this.unbound) return;
      this.clearAllMapListeners();
      // No measure layer created (query-only)
      this.onMapClickBound();
    },
    /**
       * This function is executed, after the map is bound (see mixins/Mapable)
       */
    onMapUnbound () {
      this.clearAllMapListeners();
    },
    onMapClickBound () {
      if (this.insureType === 'searchPlot') {
        this.clearSingleClickListener();
        this.singleClickKey = this.map.on('singleclick', event => this.onPlotClick(event, this.sourceTile));
      }
    },
    /**
     * Callback function executed when user clicks on the map.
     *
     * @param  {ol/MapBrowserEvent} e The map browser event
     * @param  {ol/source/TileWMS} layerSource The source of the clicked layer
     */
    async onPlotClick (e, layerSource) {
      const self = this;

      if (!self.map) {
        console.error('Map is not defined');
        return;
      }

      const view = self.map.getView();
      if (!view) {
        console.error('View is not defined');
        return;
      }

      const viewResolution = view.getResolution();
      if (!viewResolution) {
        console.error('Resolution is not defined');
        return;
      }

      const overlay = self.map.getOverlayById('popup');
      if (overlay) {
        overlay.setPosition(undefined);
      }

      const clickEvent = e;
      const landlayer = self.map.getLayers().getArray().find(layer => layer.get('lid') === 'underwritten_plots');

      if (!landlayer) {
        // console.warn('Layer "underwritten_plots" not found');
        return;
      }

      const landlayerSource = landlayer.getSource();
      const url = `${landlayerSource.getFeatureInfoUrl(
        clickEvent.coordinate,
        viewResolution,
        'EPSG:4326',
        { INFO_FORMAT: 'application/json' }
      )}`;

      const credentials = btoa('data_services:data_services');

      try {
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Basic ${credentials}`
          }
        });
        const ret = await response.json();
        if (ret && ret.features) {
          const feature = ret.features[0];
          feature.properties.name = feature.properties.land_name;
          // Removed highlight layer handling in query-only mode

          self.selectedItem = {
            ...feature.properties,
            id: feature.id.split('.')[1]
          };
          // Fetch policy information for the selected land
          if (feature.properties.insured) {
            await this.fetchPolicyInfo(self.selectedItem.land_id);
          } else {
            this.policyInfo = null;
          }

          self.trusteePopup(clickEvent.coordinate, self.selectedItem);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    },
    /**
     * Fetch policy information for the selected land
     *
     * @param {string} landId The ID of the land/plot
     */
    async fetchPolicyInfo (landId) {
      try {
        // Reset policy info
        this.policyInfo = null;
        const submitData = {
          land_id: parseInt(landId)
        };
        const response = await axios.post('/policies/plot-detail', submitData);

        const result = response.data;

        if (result.success && result.data && result.data.length > 0) {
          this.policyInfo = result.data[0];
          console.log('Policy info retrieved:', this.policyInfo);

          // If policy has insured information, update the selectedItem
          if (this.policyInfo.insured_id && this.policyInfo.insured_name) {
            this.selectedItem.insured_id = this.policyInfo.insured_id;
            this.selectedItem.insured_name = this.policyInfo.insured_name;
            this.selectedItem.crop_id = this.policyInfo.crop_id;
            this.selectedItem.crop_name = this.policyInfo.crop_name;
          }
        } else {
          console.log('No policy information found for land ID:', landId);
        }
      } catch (error) {
        console.error('Error fetching policy information:', error);
      }
    },
    async trusteeSubmit () {
      if (this.trusteeLands.length === 0) {
        this.$notify({
          title: 'ERROR!',
          text: '请先选择地块！'
        });
        return; // 如果没有选择地块，提前返回
      }
      // 完成提交逻辑
      try {
        // 这里添加实际的提交逻辑
        console.log('提交地块信息', this.trusteeLands);
        this.$notify({
          title: 'SUCCESS',
          text: '提交成功！'
        });
      } catch (error) {
        console.error('提交失败:', error);
        this.$notify({
          title: 'ERROR',
          text: '提交失败，请稍后再试！'
        });
      }
    },
    /**
     * Show the popup for the trustee lands
     *
     * @param  {ol/coordinate} coordinate The coordinate of the popup
     * @param  {ol/Feature} record The feature of the popup
     */
    trusteePopup (coordinate, record) {
      const overlayId = 'ks-popup-tooltip';
      record.objectType = 'underwritten_plots';
      // show popup and update content
      WguEventBus.$emit(overlayId + '-update-overlay', true, coordinate, {
        feature: record,
        layer: this.layer,
        hoverAttribute: true
      });
    },
    /**
     * Refresh the trustee lands
     */
    handleRefreshTrusteeLands () {
      this.trusteeLands = [];
      const landLayer = this.map.getLayers().getArray().find(layer => layer.get('lid') === 'underwritten_plots');
      if (landLayer) {
        // const landLayerSource = landLayer.getSource();
        // landLayerSource.refresh(); // Or logic to reload
      }
    }
  }
}
</script>
<style scoped></style>
